import React, {useState} from "react"
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material"

import {phraseWaitStatusMidSentence} from "./waitPartyPhrasing"

import {api} from "@indebted/api"
import type {CaseWaitParty} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"

type ReturnCaseDialogProps = {
	open: boolean
	customerID: string
	caseID: string
	customerName?: string
	waitParty: CaseWaitParty
	agentEmail: string
	agentName: string
	onClose: () => void
	onReturned: () => void
}

function ReturnCaseDialog({
	open,
	customerID,
	caseID,
	customerName,
	waitParty,
	agentEmail,
	agentName,
	onClose,
	onReturned,
}: ReturnCaseDialogProps) {
	const {notification} = useNotification()
	const [returning, setReturning] = useState(false)

	const returnToQueue = () => {
		setReturning(true)
		api.customer
			.returnCase(customerID, caseID, {
				WaitParty: waitParty.ID,
				AgentName: agentName,
				AgentEmail: agentEmail,
			})
			.then(() => {
				notification.success("Case returned")
				onReturned()
			})
			.catch((err) => notification.error(err?.Message ?? "Failed to return case"))
			.finally(() => {
				setReturning(false)
				onClose()
			})
	}

	return (
		<Dialog
			open={open}
			onClose={onClose}
			// The queue renders this inside a card that opens the case on click, and a
			// portalled dialog still bubbles its clicks up the React tree.
			onClick={(e) => e.stopPropagation()}
			maxWidth="xs"
			fullWidth
		>
			<DialogTitle>Return this case to the queue?</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary">
					This case{customerName ? ` for ${customerName}` : ""} is {phraseWaitStatusMidSentence(waitParty)}.
					Returning it ends that wait and puts the case back among the ones needing attention.
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} size="small">
					Cancel
				</Button>
				<Button variant="contained" size="small" disabled={returning} onClick={returnToQueue}>
					Return case
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export {ReturnCaseDialog}
